import React, { useState, useEffect } from 'react';
import { getAppLocale } from '../utils/locale';
import { useSettings } from '../contexts/SettingsContext';
import { CURRENCY_OPTIONS, convertCurrency, currencySymbol, isCurrencySet, type CurrencyCode } from '../utils/currency';
import { BUDGET_CALCULATOR_CATEGORIES } from '../constants/budgetCategories';
import EmptyState from './EmptyState';
import ConfirmDialog from './ui/ConfirmDialog';
import SearchableSelect from './ui/SearchableSelect';
import '../styles/components/BudgetCalculator.css';

export interface BudgetLineItem {
  id: string;
  category: string;
  description: string;
  amount: number;
}

interface BudgetTemplate {
  name: string;
  items: Omit<BudgetLineItem, 'id'>[];
}

const budgetCategories = BUDGET_CALCULATOR_CATEGORIES;

const defaultTemplates: BudgetTemplate[] = [
  {
    name: 'Traditional Nigerian Wedding',
    items: [
      { category: 'Venue', description: 'Reception hall, decoration', amount: 1500000 },
      { category: 'Catering', description: 'Food and drinks', amount: 1000000 },
      { category: 'Photography', description: 'Photos and videos', amount: 400000 },
      { category: 'Attire', description: 'Wedding dress, suit, accessories', amount: 600000 },
      { category: 'Music/DJ', description: 'Entertainment and sound', amount: 250000 },
      { category: 'Decorations', description: 'Flowers, centerpieces, lighting', amount: 400000 },
    ]
  },
  {
    name: 'Intimate Wedding',
    items: [
      { category: 'Venue', description: 'Small hall or garden', amount: 500000 },
      { category: 'Catering', description: 'Food and drinks', amount: 300000 },
      { category: 'Photography', description: 'Photos and videos', amount: 250000 },
      { category: 'Attire', description: 'Wedding outfits', amount: 400000 },
    ]
  }
];

interface BudgetCalculatorProps {
  // Notifies a parent whenever the line items change — used by the onboarding
  // wizard, which owns persistence for its own step instead of this
  // component saving anything itself.
  onItemsChange?: (items: BudgetLineItem[]) => void;
}

const BudgetCalculator: React.FC<BudgetCalculatorProps> = ({ onItemsChange }) => {
  const { settings, formatCurrency, updateSettings } = useSettings();
  const currency = settings.currency;

  const setCurrency = (c: CurrencyCode) => updateSettings({ currency: c });

  const [lineItems, setLineItems] = useState<BudgetLineItem[]>([
    { id: '1', category: 'Venue', description: 'Reception hall, decoration', amount: 1500000 },
    { id: '2', category: 'Catering', description: 'Food and drinks', amount: 1000000 },
  ]);

  useEffect(() => {
    onItemsChange?.(lineItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [clearAllConfirm, setClearAllConfirm] = useState(false);

  const addLineItem = () => {
    const newItem: BudgetLineItem = {
      id: Date.now().toString(),
      category: '',
      description: '',
      amount: 0
    };
    setLineItems([...lineItems, newItem]);
    setEditingId(newItem.id);
  };

  const removeLineItem = (id: string) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const updateLineItem = (id: string, field: keyof BudgetLineItem, value: string | number) => {
    setLineItems(lineItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const loadTemplate = (template: BudgetTemplate) => {
    const newItems: BudgetLineItem[] = template.items.map((item, index) => ({
      id: Date.now().toString() + index,
      ...item
    }));
    setLineItems(newItems);
    setShowTemplates(false);
  };

  const totalAmount = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  const formatLocalCurrency = (amount: number, curr?: string): string => {
    return formatCurrency(amount, curr || currency);
  };

  const parseCurrencyInput = (value: string): number => {
    // Remove currency symbols and commas, then parse
    const cleaned = value.replace(/[^\d.-]/g, '').trim();
    return Number(cleaned) || 0;
  };

  const handleAmountChange = (id: string, value: string) => {
    const numericValue = parseCurrencyInput(value);
    updateLineItem(id, 'amount', numericValue);
  };

  const duplicateLineItem = (item: BudgetLineItem) => {
    const newItem: BudgetLineItem = {
      ...item,
      id: Date.now().toString(),
    };
    setLineItems([...lineItems, newItem]);
  };

  const clearAll = () => {
    setClearAllConfirm(true);
  };

  return (
    <div className="wt-container fade-in">
      <div className="header-actions">
        <div className="currency-toggle">
          {CURRENCY_OPTIONS.map(option => (
            <button
              key={option.value}
              className={currency === option.value ? 'active' : ''}
              onClick={() => setCurrency(option.value as CurrencyCode)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button className="btn-add-item" onClick={addLineItem}>
          <span className="btn-icon">+</span> Add Line Item
        </button>
        {lineItems.length > 0 && (
          <button className="btn-clear" onClick={clearAll}>Clear All</button>
        )}
        <button className="btn-template" onClick={() => setShowTemplates(!showTemplates)}>
          Load Template
        </button>
      </div>
      <div className="budget-calculator">

      {showTemplates && (
        <div className="template-selector">
          <h3>Choose a Template</h3>
          <div className="template-list">
            {defaultTemplates.map((template, index) => (
              <div key={index} className="template-card category-card" onClick={() => loadTemplate(template)}>
                <h4>{template.name}</h4>
                <p>{template.items.length} items</p>
                <p className="template-total">{formatLocalCurrency(template.items.reduce((sum, item) => sum + item.amount, 0))}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="budget-configurator">

        <div className="line-items-container">
          {lineItems.length === 0 ? (
            <EmptyState title='No items yet. Click "Add Line Item" to start building your budget.' compact />
          ) : (
            <div className="line-items-table">
              <div className="table-header">
                <div className="col-category">Category</div>
                <div className="col-description">Description</div>
                <div className="col-amount">Amount ({currency})</div>
                <div className="col-percentage">% of Total</div>
                <div className="col-actions">Actions</div>
              </div>

              {lineItems.map((item) => {
                const percentage = totalAmount > 0 ? ((item.amount / totalAmount) * 100).toFixed(1) : '0.0';
                const isEditing = editingId === item.id;

                return (
                  <div key={item.id} className={`line-item ${isEditing ? 'editing' : ''}`}>
                    <div className="col-category">
                      <SearchableSelect
                        value={item.category}
                        onChange={(value) => updateLineItem(item.id, 'category', value)}
                        className="input-category"
                        placeholder="Select category..."
                        options={budgetCategories.map(cat => ({ value: cat, label: cat }))}
                      />
                    </div>
                    <div className="col-description">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        placeholder="Description"
                        className="input-description"
                        onFocus={() => setEditingId(item.id)}
                        onBlur={() => setEditingId(null)}
                      />
                    </div>
                    <div className="col-amount">
                      <div className="currency-input">
                        <span className="currency-symbol">
                          {currencySymbol(currency)}
                        </span>
                        <input
                          type="text"
                          value={item.amount ? item.amount.toLocaleString(getAppLocale()) : ''}
                          onChange={(e) => handleAmountChange(item.id, e.target.value)}
                          placeholder="0"
                          className="input-amount"
                          onFocus={() => setEditingId(item.id)}
                          onBlur={() => setEditingId(null)}
                        />
                      </div>
                    </div>
                    <div className="col-percentage">
                      <span className="percentage-badge">{percentage}%</span>
                      <div className="percentage-bar">
                        <div
                          className="percentage-fill"
                          style={ { width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="col-actions">
                      <button
                        className="budget-calc-icon-action btn-duplicate"
                        onClick={() => duplicateLineItem(item)}
                        title="Duplicate"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </button>
                      <button
                        className="budget-calc-icon-action btn-delete"
                        onClick={() => removeLineItem(item.id)}
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="budget-summary">
          <div className="summary-card">
            <div className="summary-row">
              <span className="summary-label">Total Items:</span>
              <span className="summary-value">{lineItems.length}</span>
            </div>
            <div className="summary-row total-row">
              <span className="summary-label">Total Budget:</span>
              <span className="summary-value total-amount">{formatLocalCurrency(totalAmount)}</span>
            </div>
            {isCurrencySet(currency) && currency !== 'USD' && (
              <div className="summary-row conversion-row">
                <span className="summary-label">Equivalent (USD):</span>
                <span className="summary-value">{formatLocalCurrency(convertCurrency(totalAmount, currency, 'USD'), 'USD')}</span>
              </div>
            )}
          </div>

          <div className="breakdown-chart">
            <h3>Budget Breakdown</h3>
            {lineItems.length > 0 ? (
              <div className="chart-container">
                {lineItems
                  .filter(item => item.amount > 0)
                  .sort((a, b) => b.amount - a.amount)
                  .map((item) => {
                    const percentage = totalAmount > 0 ? ((item.amount / totalAmount) * 100) : 0;
                    return (
                      <div key={item.id} className="chart-bar-row">
                        <div className="chart-label">
                          <span className="chart-category">{item.category || 'Uncategorized'}</span>
                          <span className="chart-amount">{formatCurrency(item.amount)}</span>
                        </div>
                        <div className="chart-bar">
                          <div
                            className="chart-bar-fill"
                            style={ { width: `${percentage}%` }}
                          >
                            <span className="chart-percentage">{percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="chart-empty">No data to display</p>
            )}
          </div>
        </div>
      </div>

      <div className="comparison-footer">
        <p><strong>Note:</strong> This is a planning tool. Actual costs may vary based on location, vendor selection, and specific requirements. {isCurrencySet(currency) && currency !== 'USD' && (
          <> Exchange rate: {currencySymbol(currency)}{convertCurrency(1, 'USD', currency).toLocaleString(getAppLocale(), { maximumFractionDigits: 2 })} = $1</>
        )}</p>
      </div>
      <ConfirmDialog
        open={clearAllConfirm}
        onClose={() => setClearAllConfirm(false)}
        onConfirm={() => {
          setLineItems([]);
          setClearAllConfirm(false);
        }}
        title="Clear all items?"
        description="This will remove all budget line items. This cannot be undone."
        confirmLabel="Clear All"
      />
      </div>
    </div>
  );
};

export default BudgetCalculator;
